import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const EngineeringIntelligence = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section className="section-padding-mobile bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 relative overflow-hidden">
      {/* Optimized background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-48 h-48 xs:w-96 xs:h-96 bg-primary-500/8 rounded-full"></div>
        <div className="absolute bottom-1/4 right-1/4 w-40 h-40 xs:w-80 xs:h-80 bg-primary-300/8 rounded-full"></div>
      </div>

      <div className="container-mobile relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center text-white"
        >

          <motion.h2
            id="engineering-intelligence-heading"
            className="text-mobile-title font-bold mb-6 xs:mb-8 leading-tight gpu-accelerated"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Drive Smarter Engineering Through{' '}
            <span className="bg-gradient-to-r from-white to-primary-200 bg-clip-text text-transparent">
              Lifecycle Intelligence
            </span>
          </motion.h2>

          <motion.p
            className="text-mobile-body font-light max-w-4xl mx-auto mb-8 xs:mb-12 text-primary-100 leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            CIVIONIKS empowers your projects with end-to-end lifecycle management — from design to decommissioning. 
            Our platform enhances engineering efficiency, ensures data continuity, and unlocks long-term value 
            across every phase of infrastructure development.
          </motion.p>

          {/* Optimized feature highlights */}
          <motion.div
            className="grid-mobile-3 gap-4 xs:gap-6 lg:gap-8 mb-8 xs:mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div className="bg-white/10 backdrop-blur-optimized rounded-xl xs:rounded-2xl p-4 xs:p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 gpu-accelerated">
              <div className="w-10 h-10 xs:w-12 xs:h-12 bg-primary-200 rounded-lg xs:rounded-xl flex items-center justify-center text-primary-800 mb-3 xs:mb-4 mx-auto shadow-mobile-soft xs:shadow-soft">
                <svg className="w-5 h-5 xs:w-6 xs:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-base xs:text-lg font-semibold mb-2">Enhanced Efficiency</h3>
              <p className="text-primary-100 text-sm xs:text-base leading-relaxed">Streamlined workflows and automated processes reduce manual overhead</p>
            </div>

            <div className="bg-white/10 backdrop-blur-optimized rounded-xl xs:rounded-2xl p-4 xs:p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 gpu-accelerated">
              <div className="w-10 h-10 xs:w-12 xs:h-12 bg-primary-200 rounded-lg xs:rounded-xl flex items-center justify-center text-primary-800 mb-3 xs:mb-4 mx-auto shadow-mobile-soft xs:shadow-soft">
                <svg className="w-5 h-5 xs:w-6 xs:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-base xs:text-lg font-semibold mb-2">Data Continuity</h3>
              <p className="text-primary-100 text-sm xs:text-base leading-relaxed">Seamless data preservation across project phases and stakeholders</p>
            </div>

            <div className="bg-white/10 backdrop-blur-optimized rounded-xl xs:rounded-2xl p-4 xs:p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 gpu-accelerated">
              <div className="w-10 h-10 xs:w-12 xs:h-12 bg-primary-200 rounded-lg xs:rounded-xl flex items-center justify-center text-primary-800 mb-3 xs:mb-4 mx-auto shadow-mobile-soft xs:shadow-soft">
                <svg className="w-5 h-5 xs:w-6 xs:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
              </div>
              <h3 className="text-base xs:text-lg font-semibold mb-2">Long-term Value</h3>
              <p className="text-primary-100 text-sm xs:text-base leading-relaxed">Unlock insights that drive value throughout infrastructure lifecycle</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <button
              onClick={() => {
                const element = document.getElementById('api-access');
                element?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="group inline-flex items-center justify-center gap-2 bg-white text-primary-800 font-semibold py-4 px-6 xs:px-8 lg:px-10 rounded-xl text-base xs:text-lg hover:bg-primary-50 transition-all duration-300 shadow-mobile-large xs:shadow-xl hover:shadow-2xl transform hover:-translate-y-1 active:translate-y-0 focus:ring-4 focus:ring-white/30 focus:outline-none min-h-touch-md gpu-accelerated"
            >
              API ACCESS
              <svg className="w-4 h-4 xs:w-5 xs:h-5 group-hover:translate-x-0.5 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </motion.div>
        </motion.div>
      </div>

      {/* Section divider */}
      <div className="section-divider mt-16 xs:mt-20 lg:mt-24"></div>
    </section>
  );
};

export default EngineeringIntelligence; 